import { Node } from 'prosemirror-model'
import { Decoration } from 'prosemirror-view'

import { ScriptFormat } from '@showrunner/codex'
import { constants } from '@showrunner/prose-schemas'

import { isManualBreak } from '#util/pagination/helpers'

import styles from '../split-points.module.css'

const { NodeTypeMap } = constants

interface BlockGroup {
  from: number
  to: number
}

export interface BlockSplitPoint {
  decoFrom: number
  decoTo: number
  groupStart: number
  groupEnd: number
}

export interface InlineSplitPoint {
  from: number
  to: number
}

export class BlockGroups {
  constructor(private readonly points: BlockSplitPoint[]) {}

  createDecorations(): Decoration[] {
    return this.points.map((point) =>
      createBlockDecoration({ from: point.decoFrom, to: point.decoTo }),
    )
  }

  findGroup(pos: number): BlockGroup | null {
    for (const point of this.points) {
      if (point.groupStart <= pos && pos < point.groupEnd) {
        return { from: point.groupStart, to: point.groupEnd }
      }
    }
    return null
  }
}

export class InlineSplitPoints {
  constructor(private readonly points: InlineSplitPoint[]) {}

  createDecorations(): Decoration[] {
    return this.points.map((point) =>
      createInlineDecoration({ from: point.from, to: point.to }),
    )
  }

  filter(from: number, to: number): InlineSplitPoint[] {
    return this.points.filter((point) => point.from >= from && point.to <= to)
  }
}

// get split points for pagination
// split points are of two types: block split points and inline split points
// block split points are the positions where a page break can occur between two block nodes
// inline split points are the positions where a page break can occur within a block node,
// typically after a sentence-ending punctuation mark
export function getSplitPoints(
  doc: Node,
  dialogueSplittingEnabled: boolean = true,
  scriptType: ScriptFormat = 'screenplay',
): {
  blockSplitPoints: BlockGroups
  inlineSplitPoints: InlineSplitPoints
} {
  const blockSplitPoints: {
    decoFrom: number
    decoTo: number
    groupStart: number
    groupEnd: number
  }[] = []

  const inlineSplitPoints: { from: number; to: number }[] = []
  let last: Node | null = null
  doc.descendants((parent, pageAbsPos) => {
    const isPage = parent?.type.name === NodeTypeMap.PAGE
    const isTitlePage = parent?.type.name === NodeTypeMap.TITLE_PAGE
    if (isPage || isTitlePage) {
      parent.forEach((node, localOffset) => {
        const nodeAbsPos = pageAbsPos + 1 + localOffset

        if (node.isBlock) {
          if (last && shouldJoinWithPrevious(last, node)) {
            const lastSplitPoint = blockSplitPoints.at(-1)
            if (lastSplitPoint) {
              lastSplitPoint.decoFrom = nodeAbsPos
              lastSplitPoint.decoTo = nodeAbsPos + node.nodeSize
              lastSplitPoint.groupEnd = nodeAbsPos + node.nodeSize
            }
          } else {
            blockSplitPoints.push({
              decoFrom: nodeAbsPos,
              decoTo: nodeAbsPos + node.nodeSize,
              groupStart: nodeAbsPos,
              groupEnd: nodeAbsPos + node.nodeSize,
            })
          }
          last = node
        }

        if (
          !isTitlePage &&
          canSplitInline(node, dialogueSplittingEnabled, scriptType)
        ) {
          node.descendants((child, childLocalPos) => {
            if (!child.isText || child.text === undefined) return
            let match
            REGEX.lastIndex = 0

            while ((match = REGEX.exec(child.text)) !== null) {
              const matchAbsPos = nodeAbsPos + 1 + childLocalPos + match.index
              inlineSplitPoints.push({
                from: matchAbsPos + 1,
                to: matchAbsPos + match[0].length,
              })
            }
          })
        }
      })
      return false
    }
  })

  return {
    blockSplitPoints: new BlockGroups(blockSplitPoints),
    inlineSplitPoints: new InlineSplitPoints(inlineSplitPoints),
  }
}

const createInlineDecoration = (props: { from: number; to: number }) =>
  Decoration.inline(
    props.from,
    props.to,
    { class: styles.splitPoint },
    { kind: 'inline' },
  )

const createBlockDecoration = (props: { from: number; to: number }) =>
  Decoration.node(
    props.from,
    props.to,
    { class: styles.splitPoint },
    { kind: 'block' },
  )

const INLINE_SPLIT_BLOCKS: readonly string[] = [
  NodeTypeMap.ACTION,
  NodeTypeMap.GENERAL,
  NodeTypeMap.DIALOGUE,
  NodeTypeMap.PARENTHETICAL,
]

// This list drives both split-point collection and pagination eligibility.
// Dialogue and parenthetical additionally require continuation markers to be enabled.
// Variety scripts never split blocks inline.
export function canSplitInline(
  node: Node,
  dialogueSplittingEnabled: boolean,
  scriptType: ScriptFormat = 'screenplay',
): boolean {
  if (scriptType === 'variety') return false
  if (
    (node.type.name === NodeTypeMap.DIALOGUE ||
      node.type.name === NodeTypeMap.PARENTHETICAL) &&
    !dialogueSplittingEnabled
  ) {
    return false
  }
  return INLINE_SPLIT_BLOCKS.includes(node.type.name)
}

// Whether a block is kept on the same page as the previous block.
// Never across a manual page break. A scene heading keeps the block after
// it unless that is another scene heading; other pairings are in JOIN_WITH.
function shouldJoinWithPrevious(previous: Node, block: Node): boolean {
  if (isManualBreak(block)) return false
  const sceneHeading = block.type.schema.nodes.sceneHeading
  if (previous.type === sceneHeading) {
    return block.type !== sceneHeading
  }
  return JOIN_WITH[block.type.name]?.includes(previous.type.name) ?? false
}

// [key] = [value] means that the key block type joins with
// the previous value block types.
const JOIN_WITH: { [blockType: string]: string[] } = {
  [NodeTypeMap.PARENTHETICAL]: [NodeTypeMap.CHARACTER],
  [NodeTypeMap.DIALOGUE]: [NodeTypeMap.CHARACTER],
}

const REGEX = /[.!?]\s+(?=\S)/g
